import api from '../api';
import { ProductNewModel } from '../../models/products/product-new-model';
import { CheckoutDeliveryResult } from '../products/checkout-delivery.service';

export interface SalesLocation { userId: number | null; userAddressId: number; isSelfPickup: boolean }
export interface SalesAvailability {
  pincode: string; posId: number | null; usesPosStock: boolean; catalogProductIds: number[]; outOfStockProductIds: number[];
  posCode?: string | null;
  // Optional for compatibility with an API deployed before quantity support.
  productStock?: Array<{ productId: number; availableQuantity: number }> | null;
}
export function salesStockError(error: unknown): string {
  const response = error as { response?: { data?: { message?: unknown } } };
  return typeof response?.response?.data?.message === 'string' ? response.response.data.message :
    error instanceof Error ? error.message : 'Stock could not be checked. Please retry.';
}
export async function getSalesAvailability(location: SalesLocation, signal?: AbortSignal): Promise<SalesAvailability> {
  const { data } = await api.post<SalesAvailability>('/api/Sales/DeliveryAvailability', location, { signal });
  if (!data || typeof data.pincode !== 'string' || typeof data.usesPosStock !== 'boolean' ||
    (data.posCode != null && typeof data.posCode !== 'string') ||
    !Array.isArray(data.catalogProductIds) || !Array.isArray(data.outOfStockProductIds) ||
    (data.usesPosStock && (!Number.isSafeInteger(data.posId) || Number(data.posId) <= 0)))
    throw new Error('Stock availability could not be verified.');
  if (data.usesPosStock && data.productStock === null) throw new Error('Stock quantities could not be verified.');
  if (data.productStock != null) {
    if (!Array.isArray(data.productStock) || data.productStock.some(s =>
      !s || !Number.isSafeInteger(s.productId) || s.productId <= 0 || !Number.isFinite(s.availableQuantity) || s.availableQuantity < 0))
      throw new Error('Stock quantities could not be verified.');
    const ids = new Set(data.productStock.map(s => s.productId));
    if (ids.size !== data.productStock.length || (data.usesPosStock && data.catalogProductIds.some(id => !ids.has(id))))
    throw new Error('Stock quantities could not be verified.');
  }
  return data;
}
export function salesStockLocationLabel(availability: SalesAvailability, isSelfPickup: boolean): string {
  return isSelfPickup
    ? `Stock location: ${availability.posCode?.trim() || `POS ${availability.posId}`} (your assigned POS, Self Pickup)`
    : `Stock location: ${availability.pincode}${availability.usesPosStock ? ` (mapped to ${availability.posCode?.trim() || 'POS'})` : ' (standard stock)'}`;
}
export async function checkSalesCart(location: SalesLocation, items: Array<{ id: number; quantity: number }>, signal?: AbortSignal): Promise<void> {
  if (!location.userId || (!location.isSelfPickup && !location.userAddressId) || !items.length || items.length > 1000 ||
    items.some(i => !Number.isSafeInteger(i.id) || i.id <= 0 || !Number.isFinite(i.quantity) || i.quantity <= 0))
    throw new Error('Select a customer, delivery address (or Self Pickup), and a valid cart.');
  const { data } = await api.post<CheckoutDeliveryResult>('/api/Sales/CheckDeliveryAvailability', {
    ...location, items: items.map(i => ({ productId: i.id, quantity: i.quantity }))
  }, { signal });
  if (!data || typeof data.canProceed !== 'boolean' || !Array.isArray(data.unavailableItems) || typeof data.message !== 'string')
    throw new Error('Cart stock could not be verified.');
  if (!data.canProceed || data.unavailableItems.length) throw new Error(data.message || 'Review the cart or choose a different address.');
}

export function salesAvailableQuantity(productId: number, availability: SalesAvailability | null): number | null {
  if (!availability) return null;
  if (!availability.catalogProductIds.includes(productId) || availability.outOfStockProductIds.includes(productId)) return 0;
  return availability.usesPosStock ? availability.productStock?.find(s => s.productId === productId)?.availableQuantity ?? null : null;
}

export function salesCartStockError(items: Array<{ id: number; quantity: number }>, availability: SalesAvailability): string | undefined {
  const quantities = new Map<number, number>();
  for (const item of items) {
    if (!Number.isSafeInteger(item.id) || item.id <= 0 || !Number.isFinite(item.quantity) || item.quantity <= 0)
      return 'Enter a valid quantity for each cart item.';
    quantities.set(item.id, (quantities.get(item.id) ?? 0) + item.quantity);
  }
  for (const [id, quantity] of quantities) {
    const available = salesAvailableQuantity(id, availability);
    if (!Number.isFinite(quantity) || (available !== null && quantity > available))
      return 'Some cart quantities exceed available stock. Reduce the highlighted quantities, remove items, or choose a different address.';
  }
}
export function salesProduct(product: ProductNewModel, availability: SalesAvailability | null): ProductNewModel {
  return { ...product, availabilityPending: !availability, inStock: product.inStock === 0 ||
    (availability && (!availability.catalogProductIds.includes(product.id) || availability.outOfStockProductIds.includes(product.id))) ? 0 : product.inStock };
}
